import type { GuvFlatRow } from '@/new_components/guv/guvHistoryService';

export const PIE_COLORS = {
  personnel: '#3b82f6',
  material: '#06b6d4',
  depreciation: '#f59e0b',
  interest: '#ef4444',
  other: '#9ca3af',
  ergebnis: '#8b5cf6',
  cogs: '#16a34a',
  selling: '#f59e0b',
  admin: '#ef4444',
  rnd: '#3b82f6',
};

export const KEY_ORDER = [
  'personnel',
  'material',
  'cogs',
  'depreciation',
  'selling',
  'admin',
  'rnd',
  'interest',
  'other',
  'ergebnis',
];

export type CostCategory = {
  id: string;
  label: string;
  color: string;
  keywords: string[];
  fallbackKeywords?: string[];
};

export const getGkvCategories = (t: any): CostCategory[] => [
  {
    id: 'personnel',
    label: t('companyDetail.costs.personnel'),
    color: PIE_COLORS.personnel,
    keywords: ['Personalaufwand'],
    fallbackKeywords: ['Loehne und Gehaelter', 'Soziale Abgaben'],
  },
  {
    id: 'material',
    label: t('companyDetail.costs.material'),
    color: PIE_COLORS.material,
    keywords: ['Materialaufwand'],
    fallbackKeywords: ['Aufwendungen fuer Roh-', 'bezogene Waren'],
  },
  {
    id: 'depreciation',
    label: t('companyDetail.costs.depreciation'),
    color: PIE_COLORS.depreciation,
    keywords: ['Abschreibungen'],
    fallbackKeywords: [],
  },
  {
    id: 'interest',
    label: t('companyDetail.costs.interest'),
    color: PIE_COLORS.interest,
    keywords: ['Zinsen und aehnliche Aufwendungen'],
    fallbackKeywords: [],
  },
];

export const getUkvCategories = (t: any): CostCategory[] => [
  {
    id: 'cogs',
    label: t('companyDetail.costs.cogs'),
    color: PIE_COLORS.cogs,
    keywords: ['Herstellungskosten'],
    fallbackKeywords: [],
  },
  {
    id: 'selling',
    label: t('companyDetail.costs.selling'),
    color: PIE_COLORS.selling,
    keywords: ['Vertriebskosten'],
    fallbackKeywords: [],
  },
  {
    id: 'admin',
    label: t('companyDetail.costs.admin'),
    color: PIE_COLORS.admin,
    keywords: ['Verwaltungskosten'],
    fallbackKeywords: [
      'Sonstige Verwaltungskosten',
      'Allgemeine Verwaltungskosten',
    ],
  },
  {
    id: 'rnd',
    label: t('companyDetail.costs.rnd'),
    color: PIE_COLORS.rnd,
    keywords: ['Forschung und Entwicklung', 'Forschung Und Entwicklung'],
    fallbackKeywords: ['Aufwendungen Fuer Forschung'],
  },
];

export function isUkvStyle(guvData: GuvFlatRow[]): boolean {
  return guvData.some((d) => {
    const text = [
      d.section,
      d.subsection,
      d.subsubsection,
      d.subsubsubsection,
      d.title,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return text.includes('herstellungskosten');
  });
}

/** Extract cost values for a single year from guvData. */
export function extractCostsForYear(
  yearData: GuvFlatRow[],
  categories: CostCategory[],
): { id: string; value: number }[] {
  return categories.map((cat) => {
    const matchByKeywords = (kws: string[]) =>
      yearData.filter((d) => {
        const text = [
          d.section,
          d.subsection,
          d.subsubsection,
          d.subsubsubsection,
          d.title,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return kws.some((kw) => text.includes(kw.toLowerCase()));
      });

    let matches = matchByKeywords(cat.keywords);
    const aggregateMatches = matches.filter((d) =>
      cat.keywords.some((kw) =>
        (d.title?.toLowerCase() || '').includes(kw.toLowerCase()),
      ),
    );
    if (aggregateMatches.length > 0) matches = aggregateMatches;
    if (matches.length === 0 && cat.fallbackKeywords?.length) {
      matches = matchByKeywords(cat.fallbackKeywords);
    }

    const value = matches.reduce(
      (sum, m) => sum + Math.abs(m.current_value || 0),
      0,
    );
    return { id: cat.id, value };
  });
}
